import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui';

/**
 * Barrera de errores.
 *
 * POR QUÉ EXISTE: un fallo al pintar CUALQUIER componente dejaba la aplicación
 * entera en negro. React desmonta todo el árbol cuando nadie captura la
 * excepción, así que un descuido en el desplegable de un diálogo —leer un campo
 * que llega con otro nombre— no rompía ese diálogo: rompía Obralink. Sin menú,
 * sin poder volver atrás, sin nada que leer salvo una pantalla vacía.
 *
 * Se descubrió justamente así, y no en una prueba: mirando la pantalla.
 *
 * VA DENTRO DEL LAYOUT Y NO ENVOLVIENDO TODA LA APLICACIÓN, a propósito. Lo
 * que se cae es el contenido de la página; el menú lateral y la barra superior
 * siguen ahí, así que quien se lo encuentra puede irse a otro sitio en lugar de
 * recargar a ciegas. Una barrera en la raíz protege igual pero deja la misma
 * pantalla vacía.
 *
 * Tiene que ser una clase: `componentDidCatch` no tiene equivalente con
 * ganchos, y en React 19 sigue sin tenerlo.
 */

interface Props {
  children: ReactNode;
  /** Cambia al navegar: sirve para reponerse solo al cambiar de pantalla. */
  clave?: string;
}

interface Estado {
  error: Error | null;
}

export class BarreraDeErrores extends Component<Props, Estado> {
  override state: Estado = { error: null };

  static getDerivedStateFromError(error: Error): Estado {
    return { error };
  }

  override componentDidUpdate(anterior: Props) {
    // Al cambiar de pantalla se olvida el error. Si no, quien se topa con una
    // página rota se lleva la pantalla de error a todas las demás.
    if (this.state.error && anterior.clave !== this.props.clave) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Se deja en la consola con la pila de componentes: el mensaje solo no
    // dice en qué parte del árbol pasó.
    console.error('Fallo al pintar la pantalla:', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
        <div className="bg-danger-soft text-danger mb-4 flex size-12 items-center justify-center rounded-full">
          <AlertTriangle className="size-6" />
        </div>

        <p className="text-fg font-medium">Esta pantalla ha fallado</p>
        <p className="text-fg-muted mt-1 max-w-md text-sm">
          El resto de la aplicación sigue funcionando: puedes irte a otra sección desde el menú.
        </p>

        {/* El mensaje técnico se enseña, no se esconde. Quien avisa de un
            fallo puede copiarlo, y eso ahorra media hora de «no sé qué pasó». */}
        <p className="text-fg-subtle mt-3 max-w-md font-mono text-xs break-words">
          {this.state.error.message}
        </p>

        <Button className="mt-5" onClick={() => this.setState({ error: null })}>
          <RotateCcw />
          Volver a intentarlo
        </Button>
      </div>
    );
  }
}
